import { marketingColors as C, marketingFonts as F, visualTokens as V } from '../../theme_landing_page_2';
import React from 'react';

/** Original 4-point sparkle icon — replaces the Catalis template's star-blue.svg. */
export const SparkleIcon: React.FC<{ size?: number; color?: string }> = ({ size = 24, color = C.accent }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path
      d="M12 2c.6 3.6 1.9 6.2 4 8.2 2.1 1.9 4.6 3 8 3.5-3.4.5-5.9 1.6-8 3.5-2.1 2-3.4 4.6-4 8.2-.6-3.6-1.9-6.2-4-8.2-2.1-1.9-4.6-3-8-3.5 3.4-.5 5.9-1.6 8-3.5 2.1-2 3.4-4.6 4-8.2Z"
      fill={color}
    />
  </svg>
);

/** Original checkmark icon — replaces the Catalis template's pricing-check.svg. */
export const CheckIcon: React.FC<{ size?: number; color?: string }> = ({ size = 18, color = C.accent }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="11" fill={color} fillOpacity={0.12} />
    <path d="m7 12.5 3.2 3.2L17 9" stroke={color} strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" fill="none" />
  </svg>
);

/** Original quotation-mark icon — replaces the Catalis template's quote-icon.svg. */
export const QuoteIcon: React.FC<{ size?: number; color?: string }> = ({ size = 40, color = V.legacyInkAlpha12 }) => (
  <svg width={size} height={size} viewBox="0 0 40 40" fill="none">
    <path
      d="M15.5 10c-4.4 1.9-7 5.6-7 10.3 0 3.7 2.3 6.2 5.3 6.2 2.7 0 4.7-2 4.7-4.6 0-2.4-1.7-4.2-4-4.2-.4 0-.8 0-1 .1.4-2.8 2.4-5.2 5.2-6.5L15.5 10Zm14 0c-4.4 1.9-7 5.6-7 10.3 0 3.7 2.3 6.2 5.3 6.2 2.7 0 4.7-2 4.7-4.6 0-2.4-1.7-4.2-4-4.2-.4 0-.8 0-1 .1.4-2.8 2.4-5.2 5.2-6.5L29.5 10Z"
      fill={color}
    />
  </svg>
);

/**
 * Circular initials avatar — replaces the Catalis template's testimonial headshot photos.
 * These testimonials are placeholder/example copy, not real photographed customers, so an
 * initials avatar (same treatment already used for the accountant-access example on
 * AccountingCentrePage) is both self-contained and more honest than a stranger's stock photo.
 */
export const InitialsAvatar: React.FC<{ name: string; size?: number; style?: React.CSSProperties }> = ({ name, size = 48, style }) => {
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join('');
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: C.avatarBg,
        color: C.avatarText,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: 'inherit',
        fontWeight: V.fontWeight600,
        fontSize: size * 0.36,
        flex: 'none',
        ...style,
      }}
    >
      {initials}
    </div>
  );
};

const MILL_BLADE_COLORS = [V.brandBlue, V.brandCyan, V.brandViolet, V.brandTeal];

/**
 * MillOS brand mark — a 4-blade windmill, original geometry (simple procedural paths, not
 * traced from any third-party asset). Ties the "MillOS" name ("grinding through compliance")
 * to a literal mark; reused at every size across nav/footer/hero/orbit rather than a raster
 * logo file, so it's crisp at any scale and has zero external asset dependency.
 */
export const MillosMark: React.FC<{ size?: number; rotation?: number; style?: React.CSSProperties }> = ({ size = 48, rotation = 0, style }) => (
  <svg width={size} height={size} viewBox="-50 -50 100 100" style={style}>
    <g style={{ transform: `rotate(${rotation}deg)`, transformOrigin: '0 0' }}>
      {MILL_BLADE_COLORS.map((color, i) => (
        <g key={color} transform={`rotate(${i * 90})`}>
          <g style={{ transform: 'scaleX(var(--website-mill-blade-direction, 1))' }}>
            <path d="M-4 -4 L-10 -42 C 10 -48 30 -40 40 -26 Z" fill={color} />
            <path d="M-4 -4 L-10 -42 L6 -30 Z" fill={V.black} opacity={0.2} />
          </g>
        </g>
      ))}
    </g>
  </svg>
);

/** "Mill" + "OS" wordmark, matching the nav/footer treatment (navy "Mill", cyan "OS"). */
export const MillosWordmark: React.FC<{ fontSize?: number | string; style?: React.CSSProperties }> = ({ fontSize = V.wordmarkDefaultSize, style }) => (
  <span style={{ fontFamily: F.wordmark, fontWeight: V.fontWeight600, fontSize, letterSpacing: V.letterSpacing_0_01em, color: V.ink, ...style }}>
    Mill<span style={{ color: V.brandCyan }}>OS</span>
  </span>
);

/**
 * Decorative repeating terrace-house silhouette motif — original line art, replaces the
 * Catalis template's "uk-terrace-motif.svg" asset (never shipped to us; same third-party-CDN
 * risk as every other asset already self-hosted/replaced on this site).
 */
export const UkTerraceMotif: React.FC<{ width?: number | string; style?: React.CSSProperties; color?: string }> = ({ width = 200, style, color = 'currentColor' }) => (
  <svg width={width} viewBox="0 0 560 120" fill="none" style={style} preserveAspectRatio="xMidYMax meet">
    <g fill={color}>
      {Array.from({ length: 8 }).map((_, i) => {
        const x = i * 70;
        return (
          <path
            key={i}
            d={`M${x} 120 V64 L${x + 35} 40 L${x + 70} 64 V120 Z M${x + 14} 120 V86 H${x + 26} V120 M${x + 48} 120 V96 H${x + 58} V120`}
            fillRule="evenodd"
          />
        );
      })}
    </g>
  </svg>
);

/**
 * Decorative corner flourish — concentric quarter-rings fading out from the corner, with two
 * small rotated accent squares. Original line art, replaces the Claude Design Pricing page's
 * "uk-corner-vignette.svg" asset (referenced by the uploaded design but never shipped to us).
 * Takes a `color` prop rather than relying on CSS filters, so callers can place it on a dark
 * hero (white) or a light panel (brand navy/blue) without any `filter: invert()` trick.
 */
export const UkCornerVignette: React.FC<{ width?: number | string; style?: React.CSSProperties; color?: string }> = ({ width = 200, style, color = 'currentColor' }) => (
  <svg width={width} viewBox="0 0 240 240" fill="none" style={style}>
    <g stroke={color} strokeWidth={1.4}>
      {[40, 80, 120, 160, 200].map((r) => (
        <path key={r} d={`M0 ${r} A ${r} ${r} 0 0 1 ${r} 0`} opacity={0.9 - r / 260} />
      ))}
    </g>
    <g fill={color}>
      <circle cx="0" cy="0" r="5" opacity={0.5} />
      <rect x="18" y="18" width="10" height="10" rx="2" opacity={0.35} transform="rotate(45 23 23)" />
      <rect x="54" y="54" width="7" height="7" rx="1.5" opacity={0.3} transform="rotate(45 57.5 57.5)" />
    </g>
  </svg>
);

/**
 * Decorative gradient panel with a stacked-building glyph — replaces the Catalis template's
 * "about" section photo (a stock property photo we have no rights to redistribute).
 */
export const BuildingArtPanel: React.FC<{ style?: React.CSSProperties }> = ({ style }) => (
  <div
    style={{
      width: '100%',
      aspectRatio: '4 / 5',
      borderRadius: V.borderRadius32,
      background: C.heroGradient,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      ...style,
    }}
  >
    <svg width="42%" height="42%" viewBox="0 0 96 96" fill="none">
      <rect x="10" y="30" width="30" height="56" rx="3" fill={V.paperAlpha16} />
      <rect x="46" y="14" width="40" height="72" rx="3" fill={V.paperAlpha28} />
      {[22, 34, 46].map((y) => (
        <React.Fragment key={`a${y}`}>
          <rect x="16" y={y} width="7" height="7" rx="1" fill={V.paperAlpha50} />
          <rect x="27" y={y} width="7" height="7" rx="1" fill={V.paperAlpha50} />
        </React.Fragment>
      ))}
      {[24, 36, 48, 60].map((y) => (
        <React.Fragment key={`b${y}`}>
          <rect x="52" y={y} width="7" height="7" rx="1" fill={V.paperAlpha75} />
          <rect x="64" y={y} width="7" height="7" rx="1" fill={V.paperAlpha75} />
          <rect x="76" y={y} width="7" height="7" rx="1" fill={V.paperAlpha75} />
        </React.Fragment>
      ))}
    </svg>
  </div>
);
